import { EMPTY, Observable, of, race } from 'rxjs';
import { withLatestFrom, pluck, mapTo, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { DataStatus, EpicDeps } from '@wearejh/m2-pwa-engine/lib/types';
import { AppendUserState, UserActions } from '@wearejh/m2-pwa-user';
import { LOCATION_CHANGE } from '@wearejh/m2-pwa-engine/lib/router';
import { AppendRouterState, RouterActions } from '@wearejh/m2-pwa-engine/lib/router/router.register';

import { Actions, CartMsg, Msg, STORAGE_KEY_CART, TypeMap } from '../cart.actions';

type Action = TypeMap['Cart.Refresh'];
type AllActions = Actions | UserActions | RouterActions;

/**
 * This epic will determine if the current stored cart should be updated/created
 * @param action$
 * @param state$
 * @param deps
 */
export function cartRefresh(
    action$: Observable<AllActions>,
    state$: Observable<AppendRouterState<AppendUserState>>,
    deps: EpicDeps,
): Observable<any> {
    const pathname$ = state$.pipe(pluck('router', 'location', 'pathname'));
    return action$.pipe(
        ofType<AllActions, Action>('Cart.Refresh'),
        withLatestFrom(pathname$, state$.pipe(pluck('user'))),
        switchMap(([action, pathname, user]) => {
            /**
             * If the user is currently refreshing
             *
             *  - success: reply this action
             *  - failure: do nothing
             */
            if (user.status === DataStatus.Pending) {
                // replay the action if the user data get's persisted
                const afterUser$ = action$.pipe(ofType<AllActions>('User.Persist'), mapTo(action));
                // do nothing if the user becomes signed out
                const userError$ = action$.pipe(
                    ofType<AllActions>('User.SignOut', LOCATION_CHANGE),
                    mapTo(CartMsg('Cart.Noop', `doing nothing,  'user refresh' errored or navigation occurred`)),
                );
                return race(afterUser$, userError$);
            }
            /**
             * If we're in any checkout page, just don't perform
             * any cart refreshing - instead we allow checkout implementations
             * to trigger the refresh with Msg('Cart.Fetch');
             */
            if (pathname && pathname.indexOf('/checkout') === 0 && pathname.indexOf('/checkout/success') === -1) {
                return EMPTY;
            }

            /**
             * If we have been redirected from Adyen/Klarna do nothing,
             * Instead we allow the Adyen implementations to trigger the refresh with Msg('Cart.Fetch');
             */
            if (pathname && pathname.indexOf('adyen/process/result') !== -1) {
                return EMPTY;
            }

            const existing = (deps.storage.get(STORAGE_KEY_CART) || {}).cartId;
            const wasForced = action.payload.force;

            if (existing && wasForced) {
                return of(Msg('Cart.Fetch', undefined));
            }

            if (existing && !wasForced) {
                return of(Msg('Cart.FetchSkipped'));
            }

            return of(Msg('Cart.Fetch', undefined));
        }),
    );
}
